"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const locale_1 = require("~/services/locale");
const projections_1 = require("../projections");
const utils_1 = require("~/utils/utils");
const TrackView_1 = __importDefault(require("~/view/TrackView"));
const BaseControl_1 = __importDefault(require("./BaseControl"));
require("./Track.scss");
const ga_1 = __importDefault(require("~/services/ga"));
/**
 * Configurations par défaut.
 */
const DEFAULT = {
    trackFormat: 'KML',
    trackColor: '#991616',
    trackWidth: 4,
};
/**
 * Controleur permettant d'ajouter un chemin / itinéraire sur la carte.
 */
class Track extends BaseControl_1.default {
    constructor(options) {
        super(options);
        this.permalink = options.permalink;
    }
    /**
     * Initialise le formulaire et se charge d'ajouter l'itinéraire si l'URL
     * en contient un.
     */
    draw(px) {
        super.draw(px);
        this.keepEvents(this.div);
        /** Formulaire */
        this.trackView = new TrackView_1.default();
        this.trackView.$el.on("submit", () => {
            var settings = this.trackView.getSettings();
            if (settings) {
                this.addTrack(settings);
                // Google analytics
                ga_1.default.logEvent("Map:ImportTrack");
            }
        });
        this.div.appendChild(this.trackView.$el.get(0));
        // Initialisation
        this.permalink.register(this);
        const settings = this.parsePermalink(this.permalink.getData());
        if (settings) {
            this.trackView.loadSettings(settings);
            this.addTrack(settings);
        }
        return this.div;
    }
    /**
     * Ajoute sur la carte le chemin spécifié.
     *
     * @param {TrackSettings} settings:
     * 			  Contient les objets suivants:
     * 				- trackFormat: Format de layer: KML ou GPX.
     * 				- trackUrl: L'url du fichier à charger (fichier KML ou GPX). Si l'URL n'a pas
     *            	  un format correct, un message d'erreur sera affiché à l'utilisateur.
     *              - zoomToLayer: True si on souhaite zoomer la carte sur le nouveau layer
     *              - trackColor: couleur des trais
     *              - trackWidth: épaiseur des traits
     */
    addTrack(settings) {
        // S'il y a déjà un itinéraire, on l'enlève avant d'ajouter le nouveau
        if (this.currentLayer != undefined && this.currentLayer != null) {
            this.map.removeLayer(this.currentLayer);
        }
        // On vérifie le format de l'URL
        if (!(0, utils_1.validateURL)(settings.trackUrl)) {
            alert((0, locale_1.t)('track.invalidUrl') + settings.trackUrl);
            return;
        }
        var layer = this.buildLayer(settings);
        // Le module doit être informé si l'utilisateur supprime la couche de la
        // carte
        layer.events.register('removed', layer, this.onLayerRemoved.bind(this));
        // Listener pour le chargement du layer
        layer.events.register('loadend', layer, this.onLayerLoad.bind(this));
        this.currentLayer = layer;
        this.currentSettings = settings;
        this.map.addLayer(layer);
        this.trackView.$el.trigger("loadTrackStart");
    }
    buildLayer(settings) {
        // Options génériques de la couche
        var options_layer = {
            // Options obligatoires
            strategies: [new OpenLayers.Strategy.Fixed()],
            protocol: new OpenLayers.Protocol.HTTP({
                url: (0, utils_1.proxyURL)(settings.trackUrl)
                // Il faudra ici spécifier le format, en fonction du type de données
                // (cf. ci-dessous)
            }),
            // Always set projection when setting maxExtent
            projection: projections_1.gpsProjection,
            // Options pour modifier les styles
            preFeatureInsert(f) {
                if (f) {
                    f.style = {
                        strokeColor: settings.trackColor,
                        strokeWidth: settings.trackWidth
                    };
                }
            }
        };
        // Options spécifiques au type de données
        switch (settings.trackFormat) {
            case 'KML':
                options_layer.protocol.format = new OpenLayers.Format.KML({
                    extractAttributes: true,
                    extractStyles: true
                });
                break;
            case 'GPX':
                options_layer.protocol.format = new OpenLayers.Format.GPX({
                    extractWaypoints: true,
                    extractTracks: true,
                    extractRoutes: true,
                    extractAttributes: true
                });
                break;
            default:
        }
        return new OpenLayers.Layer.Vector('Mon itinéraire', options_layer);
    }
    /**
     * Listener pour détecter qu'une couche a été retirée par l'utilisateur.
     */
    onLayerRemoved(event) {
        // Le layer a été retiré de la carte, on peut donc l'oublier et on a
        // le champ libre pour en ajouter une autre.
        if (event.layer == this.currentLayer) { // todo: layer or object?
            this.currentLayer = undefined;
            this.currentSettings = undefined;
            event.layer.destroy();
        }
    }
    /**
     * Listener pour détecter qu'une couche a été chargée.
     */
    onLayerLoad(event) {
        var _a;
        this.trackView.$el.trigger("loadTrackEnd");
        var layer = event.object;
        // On peut vérifier que la layer s'est correctement chargée: si elle
        // contient des données
        if (layer.features == undefined
            || layer.features.length == 0) {
            alert((0, locale_1.t)('track.error'));
            this.map.removeLayer(layer);
        }
        else {
            if ((_a = this.currentSettings) === null || _a === void 0 ? void 0 : _a.zoomToLayer) {
                this.map.zoomToExtent(layer.getDataExtent());
            }
        }
    }
    /**
     * Extrait les paramètres issues de l'url.
     */
    parsePermalink(data) {
        const url = data['trackUrl'];
        if (url) {
            return {
                trackUrl: decodeURIComponent(url),
                trackFormat: data['trackFormat'] ? data['trackFormat']
                    : DEFAULT.trackFormat,
                trackColor: data['trackColor'] ? '#' + data['trackColor']
                    : DEFAULT.trackColor,
                trackWidth: data['trackWidth'] ? data['trackWidth']
                    : DEFAULT.trackWidth,
                zoomToLayer: false,
            };
        }
        return null;
    }
    /**
     * Retourne les paramètres à introduire dans l'url pour reproduire le même
     * itinéraire.
     *
     * @return Le tableau data des paramètres.
     */
    providePermalink() {
        const data = {};
        if (this.currentLayer != undefined && this.currentLayer != null) {
            const settings = this.currentSettings;
            data['trackUrl'] = encodeURIComponent(settings.trackUrl);
            data['trackFormat'] = settings.trackFormat;
            if (data.trackColor != DEFAULT.trackColor)
                data['trackColor'] = settings.trackColor.slice(1); // On enlève le #
            if (data.trackWidth != DEFAULT.trackWidth)
                data['trackWidth'] = settings.trackWidth;
        }
        return data;
    }
}
exports.default = Track;
Object.defineProperties(Track.prototype, {
    CLASS_NAME: {
        value: "JGN.Control.Track"
    }
});
